import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { DragDropContext } from 'react-beautiful-dnd'
import Drawer from '../../ui/Drawer'
import Button from '../../ui/Button'
import Select from '../../ui/Select/Select'
import Tooltip from '../../ui/Tooltip'
import CenteredState from '../../ui/CenteredState'
import InfoCircleIcon from '../../ui/icons/InfoCircleIcon'
import { showToast } from '../../shared/Toast'
import Loader from '../../shared/Loader'
import IssueStatusesService from '../../../services/api/IssueStatusesService'
import CategorySection from './CategorySection'
import DeleteStatusModal from './DeleteStatusModal'
import DiscardChangesModal from './DiscardChangesModal'
import StatusChangeModal from './StatusChangeModal'
import StatusDot from './StatusDot'
import {
  CATEGORIES, MAX_PER_CATEGORY, categoryLabel, colorsForCategory, defaultColorForCategory
} from './constants'
import {
  applyMove, buildBatchPayload, flagTransfer, orderedFor, removeOrFlag, saveOutcome, toRow
} from './drawerState'

const IS_DEFAULT_KEY = 'is_default'
const ISSUES_COUNT_KEY = 'issues_count'
const DEFAULT_CATEGORY = 'to_do'
const SAVE_SUCCESS = {
  deleted: 'La suppression du statut a bien été effectuée.',
  mixed: 'Les modifications ont bien été enregistrées.',
  recategorized: 'Le changement de catégorie du statut a bien été effectué.',
  updated: 'La modification du statut a bien été effectuée.'
}
const SAVE_ERROR = {
  deleted: 'Une erreur est survenue lors de la suppression du statut.',
  mixed: 'Une erreur est survenue lors de l’enregistrement des modifications.',
  recategorized: 'Une erreur est survenue lors du changement de catégorie du statut.',
  updated: 'Une erreur est survenue lors de la modification du statut.'
}
const DEFAULT_INFO = 'Statut par défaut lors du déclenchement de l’alerte'

const FIELD_CLASS = 'tw:flex tw:flex-col tw:gap-2'
const LABEL_CLASS = 'tw:flex tw:items-center tw:gap-1 tw:text-sm tw:font-medium tw:text-gray-950 tw:m-0!'
const SEPARATOR_CLASS = 'tw:my-0! tw:border-0 tw:border-t tw:border-solid tw:border-gray-300!'
const SECTIONS_CLASS = 'tw:flex tw:flex-col tw:gap-6'
const FOOTER_CLASS = 'tw:flex! tw:items-center tw:justify-end tw:gap-2 tw:pt-4 tw:pb-6 tw:px-6 '
  + 'tw:border-t tw:border-solid tw:border-gray-300'

const renderDefaultOption = option => <StatusDot color={option.color} label={option.label} />

const EditStatusesDrawer = ({
  isOpen, onClose, collectId, statuses, loading, onSaved, toastContainerId, palette
}) => {
  const [rows, setRows] = useState([])
  const [saving, setSaving] = useState(false)
  const [deleteRow, setDeleteRow] = useState(null)
  const [pendingMove, setPendingMove] = useState(null)
  const [rowErrors, setRowErrors] = useState({})
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const tempCounter = useRef(0)
  const hasSeeded = useRef(false)
  const initialSnapshot = useRef('')

  useEffect(() => {
    if (!isOpen) {
      hasSeeded.current = false
      setRowErrors({})
      setDeleteRow(null)
      setPendingMove(null)
      setShowCancelConfirm(false)
      return
    }
    if (hasSeeded.current)
      return
    const seeded = statuses.map(toRow)
    setRows(seeded)
    initialSnapshot.current = JSON.stringify(seeded)
    tempCounter.current = 0
    if (statuses.length)
      hasSeeded.current = true
  }, [isOpen, statuses])

  const categoriesWithColors = useMemo(() => CATEGORIES
    .map(category => ({ ...category, colors: colorsForCategory(palette, category.value) })), [palette])

  const defaultOptions = useMemo(() => orderedFor(rows, DEFAULT_CATEGORY)
    .map(row => ({ value: row.key, label: row.name, color: row.color })), [rows])

  const selectedDefault = useMemo(() => {
    const current = rows.find(row => row.isDefault)
    return defaultOptions.find(option => option.value === current?.key) || null
  }, [defaultOptions, rows])

  const deleteOptions = useMemo(() => rows
    .filter(row => !row.destroyed && row.id && row.key !== deleteRow?.key)
    .map(row => ({
      value: String(row.id), label: row.name, color: row.color, category: row.category
    })), [rows, deleteRow])

  const handleNameChange = useCallback((key, name) => {
    setRows(current => current.map(row => row.key === key ? { ...row, name } : row))
    setRowErrors(current => {
      if (!current[key])
        return current
      const next = { ...current }
      delete next[key]
      return next
    })
  }, [])

  const handleColorChange = useCallback((key, color) => {
    setRows(current => current.map(row => row.key === key ? { ...row, color } : row))
  }, [])

  const handleDefaultChange = useCallback(option => {
    setRows(current => current.map(row => ({ ...row, isDefault: row.key === option.value })))
  }, [])

  const handleAdd = useCallback(category => {
    setRows(current => {
      const siblings = current.filter(row => !row.destroyed && row.category === category)
      tempCounter.current += 1
      const newRow = {
        key: `temp-${tempCounter.current}`,
        id: null,
        name: '',
        color: defaultColorForCategory(palette, category, siblings.map(row => row.color)),
        category,
        position: siblings.length,
        isDefault: false,
        issuesCount: 0
      }
      return [...current, newRow]
    })
  }, [palette])

  const handleDelete = useCallback(row => {
    if (row.id) {
      setDeleteRow(row)
      return
    }
    setRows(current => removeOrFlag(current, row))
  }, [])

  const handleDeleteConfirm = useCallback(transferValue => {
    setRows(current => flagTransfer(current, deleteRow.key, transferValue))
    setDeleteRow(null)
  }, [deleteRow])

  const handleDragEnd = useCallback(result => {
    const { source, destination } = result
    if (!destination || source.droppableId === destination.droppableId && source.index === destination.index)
      return
    const moved = orderedFor(rows, source.droppableId)[source.index]
    const crossing = source.droppableId !== destination.droppableId
    if (crossing && moved.isDefault && destination.droppableId !== DEFAULT_CATEGORY)
      return
    if (crossing && orderedFor(rows, destination.droppableId).length >= MAX_PER_CATEGORY)
      return
    const destinationColors = orderedFor(rows, destination.droppableId).map(row => row.color)
    const move = {
      key: moved.key,
      from: source.droppableId,
      to: destination.droppableId,
      index: destination.index,
      defaultColor: defaultColorForCategory(palette, destination.droppableId, destinationColors)
    }
    if (crossing && moved.issuesCount > 0) {
      setPendingMove({ ...move, name: moved.name, issuesCount: moved.issuesCount })
      return
    }
    setRows(current => applyMove(current, move))
  }, [rows, palette])

  const handleMoveConfirm = useCallback(() => {
    setRows(current => applyMove(current, pendingMove))
    setPendingMove(null)
  }, [pendingMove])

  const closeDelete = useCallback(() => setDeleteRow(null), [])
  const closeMove = useCallback(() => setPendingMove(null), [])

  const isDirty = JSON.stringify(rows) !== initialSnapshot.current

  const requestClose = useCallback(() => {
    if (isDirty) {
      setShowCancelConfirm(true)
      return
    }
    onClose()
  }, [isDirty, onClose])

  const confirmDiscard = useCallback(() => {
    setShowCancelConfirm(false)
    onClose()
  }, [onClose])

  const cancelDiscard = useCallback(() => setShowCancelConfirm(false), [])

  const applyServerErrors = useCallback(formattedErrors => {
    setRowErrors(formattedErrors)
    const knownKeys = new Set(rows.map(row => row.key))
    const orphans = Object.entries(formattedErrors)
      .filter(([key]) => !knownKeys.has(key))
      .flatMap(([, messages]) => Object.values(messages).flat())
    if (orphans.length)
      showToast(orphans.join(' '), 'error', toastContainerId)
  }, [rows, toastContainerId])

  const handleSave = useCallback(async() => {
    setSaving(true)
    setRowErrors({})
    const outcome = saveOutcome(rows, JSON.parse(initialSnapshot.current))
    try {
      const result = await IssueStatusesService.batch(collectId, buildBatchPayload(rows))
      if (result.success) {
        showToast(SAVE_SUCCESS[outcome], 'success', toastContainerId)
        onSaved()
        onClose()
        return
      }
      applyServerErrors(result.formattedErrors)
    } catch {
      showToast(SAVE_ERROR[outcome], 'error', toastContainerId)
    } finally {
      setSaving(false)
    }
  }, [rows, collectId, toastContainerId, onSaved, onClose, applyServerErrors])

  return (
    <Drawer ariaLabel="Modifier les statuts" isOpen={isOpen} onClose={requestClose}>
      <Drawer.Header closeVariant="dismiss" onClose={requestClose}>Modifier les statuts</Drawer.Header>
      <Drawer.Body>
        {loading ? <CenteredState><Loader /></CenteredState> : (
          <>
            <div className={FIELD_CLASS}>
              <span className={LABEL_CLASS}>
                Statut par défaut
                <Tooltip content={DEFAULT_INFO}>
                  <InfoCircleIcon className="tw:h-3 tw:w-3 tw:text-gray-400" />
                </Tooltip>
              </span>
              <Select
                formatOptionLabel={renderDefaultOption}
                isSearchable={false}
                options={defaultOptions}
                value={selectedDefault}
                onChange={handleDefaultChange}
              />
            </div>
            <hr className={SEPARATOR_CLASS} />
            <DragDropContext onDragEnd={handleDragEnd}>
              <div className={SECTIONS_CLASS}>
                {categoriesWithColors.map(category => (
                  <CategorySection
                    key={category.value}
                    category={category}
                    disabled={saving}
                    errors={rowErrors}
                    rows={orderedFor(rows, category.value)}
                    onAdd={handleAdd}
                    onColorChange={handleColorChange}
                    onDelete={handleDelete}
                    onNameChange={handleNameChange}
                  />
                ))}
              </div>
            </DragDropContext>
          </>
        )}
      </Drawer.Body>
      <footer className={FOOTER_CLASS}>
        <Button disabled={saving} variant="neutral" onClick={requestClose}>Annuler</Button>
        <Button disabled={saving || loading} variant="primary" onClick={handleSave}>Sauvegarder</Button>
      </footer>
      <DeleteStatusModal
        key={deleteRow?.key ?? 'delete-closed'}
        isOpen={deleteRow !== null}
        issuesCount={deleteRow?.issuesCount ?? 0}
        options={deleteOptions}
        statusName={deleteRow?.name ?? ''}
        onCancel={closeDelete}
        onConfirm={handleDeleteConfirm}
      />
      <StatusChangeModal
        fromLabel={pendingMove ? categoryLabel(pendingMove.from) : ''}
        isOpen={pendingMove !== null}
        issuesCount={pendingMove?.issuesCount ?? 0}
        statusName={pendingMove?.name ?? ''}
        toLabel={pendingMove ? categoryLabel(pendingMove.to) : ''}
        onCancel={closeMove}
        onConfirm={handleMoveConfirm}
      />
      <DiscardChangesModal
        isOpen={showCancelConfirm}
        onCancel={cancelDiscard}
        onConfirm={confirmDiscard}
      />
    </Drawer>
  )
}

EditStatusesDrawer.propTypes = {
  collectId: PropTypes.number.isRequired,
  isOpen: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  palette: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  statuses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    color: PropTypes.string,
    category: PropTypes.string.isRequired,
    position: PropTypes.number,
    [IS_DEFAULT_KEY]: PropTypes.bool,
    [ISSUES_COUNT_KEY]: PropTypes.number
  })).isRequired,
  toastContainerId: PropTypes.string.isRequired
}

EditStatusesDrawer.defaultProps = {
  palette: {}
}

export default EditStatusesDrawer
